/**
 * 트랙 안전 처리 유틸리티
 *
 * Chrome 146 CVE 보호:
 * - track.stream / track.track 직접 접근 금지
 * - track.disposed === false (strict) 체크 필수
 * - detach → 500ms 딜레이 → dispose 순서 보장
 */
import type { JitsiLocalTrack } from '@/types/jitsi';

/**
 * JitsiLocalTrack을 안전하게 dispose한다.
 * @param track - dispose할 트랙
 * @param videoEl - detach할 HTML 엘리먼트 (있는 경우)
 */
export async function safeDisposeTrack(
  track: JitsiLocalTrack,
  videoEl?: HTMLVideoElement | HTMLAudioElement
): Promise<void> {
  // 이미 disposed된 트랙은 무시
  if (track.disposed) return;

  // 엘리먼트에서 detach
  if (videoEl) {
    try {
      track.detach(videoEl);
    } catch (err) {
      console.warn('[safeDisposeTrack] detach error:', err);
    }
  }

  // Chrome 146 CVE: 500ms 딜레이 후 dispose
  await new Promise<void>((resolve) => setTimeout(resolve, 500));

  // 딜레이 후 다시 disposed 체크
  if (track.disposed) return;

  try {
    await track.dispose();
  } catch (err) {
    console.warn('[safeDisposeTrack] dispose error:', err);
  }
}

/**
 * 트랙이 사용 가능한 상태인지 확인한다.
 * track.disposed === false (strict equality) 사용 필수.
 */
export function isTrackActive(track: JitsiLocalTrack | null | undefined): boolean {
  if (!track) return false;
  // strict equality: disposed는 boolean이어야 함
  if (track.disposed !== false) return false;
  return true;
}

/**
 * 트랙이 뮤트 상태인지 안전하게 확인한다.
 * track.muted 직접 접근 대신 isMuted() 메서드 사용.
 */
export function isTrackMuted(track: JitsiLocalTrack | null | undefined): boolean {
  if (!isTrackActive(track)) return true;
  try {
    return track!.isMuted();
  } catch {
    return true;
  }
}
